// src/pages/CompanyManagement/tabs/ServicesTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Avatar
} from '@mui/material';
import {
  Apps as ServicesIcon
} from '@mui/icons-material';
import { servicesApi } from '../../../services/apiClient';

const ServicesTab = ({ company, onChange, t }) => {
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get company's allowed services
  const allowedServices = company.allowedServices || [];

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await servicesApi.getAll();

      if (response.data.success) {
        setAllServices(response.data.data || []);
      } else {
        setError('Failed to load services');
      }
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err.response?.data?.error || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceToggle = (serviceId) => {
    const isCurrentlyAllowed = allowedServices.includes(serviceId);
    const updatedServices = isCurrentlyAllowed
      ? allowedServices.filter(id => id !== serviceId)
      : [...allowedServices, serviceId];

    onChange({
      allowedServices: updatedServices
    });
  };

  const isServiceAllowed = (serviceId) => {
    return allowedServices.includes(serviceId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Company Services
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ServicesIcon color="primary" />
            <Typography variant="h6">
              Available Services
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 3 }}>
            Select which services this company has access to. Only selected services will be available to the company users.
          </Alert>

          {allServices.length === 0 ? (
            <Alert severity="warning">
              No services are currently available in the system.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {allServices.map((service) => {
                const isAllowed = isServiceAllowed(service.id);

                return (
                  <Grid item xs={12} key={service.id}>
                    <Box
                      sx={{
                        border: 1,
                        borderColor: isAllowed ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: isAllowed ? 'action.selected' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: isAllowed ? 'action.selected' : 'action.hover',
                        }
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAllowed}
                            onChange={() => handleServiceToggle(service.id)}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {service.logo && (
                              <Avatar
                                src={service.logo}
                                sx={{ width: 40, height: 40 }}
                              >
                                <ServicesIcon />
                              </Avatar>
                            )}
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body1" fontWeight="500">
                                  {service.friendlyName || service.name}
                                </Typography>
                                <Chip
                                  label={service.name}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                />
                              </Box>
                              {service.description && (
                                <Typography variant="body2" color="text.secondary">
                                  {service.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                        sx={{ m: 0, width: '100%' }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Summary */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {allowedServices.length} of {allServices.length} services enabled
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServicesTab;
